/**
 * CampaignSelectDashboard — 대시보드 사이드바용 캠페인 선택 드롭다운
 *
 * 캠페인 대시보드 사이드바용 캠페인 선택 드롭다운.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '@/components/Tooltip';
import { useI18nText } from '@/lib/utils/i18nText';
import {
  CampaignItem,
  CampaignStatusIcon,
} from '@/components/CampaignSelect';
import { getRecentCampaignIds, addRecentCampaignId, removeRecentCampaignId } from '@/lib/utils/recentCampaigns';
import { StatusFilterToggles } from '@/components/StatusFilterToggles';

export interface CampaignSelectDashboardProps {
  campaigns: CampaignItem[];
  campaignsLoading: boolean;
  selectedCampaignId: string;
  selectedCampaignLabel: string;
  onSelectCampaign: (id: string) => void;
  placeholder?: string;
  showStatusFilter?: boolean;
  /** Statuses to hide from both the campaign list and the status filter dropdown */
  excludeStatuses?: string[];
  /** 드롭다운 내 "캠페인" 대신 표시할 명칭 (예: '로스터') */
  noun?: string;
  /** 최상위 div에 추가할 클래스 (너비 제한 등) */
  className?: string;
  /** 하단 "새 로스터 생성" 버튼 콜백. 제공 시 버튼 표시 */
  onAddRoster?: () => void;
  /** 제공 시 드롭다운 상단에 "전체(필터 없음)" 항목 추가 및 트리거에 × 버튼 표시 */
  onClearFilter?: () => void;
  /** 트리거 버튼의 기본 320px 폭 제한을 해제 — 넓은 영역에 배치할 때(예: 결제 검색 필터) 사용 */
  fullWidth?: boolean;
}

type CampaignGroupKey = 'active' | 'completed' | 'other' | 'roster';

/** Map a campaign status to its dropdown group. "active" covers everything that
 * isn't finished yet (draft/private_draft/in_progress) so it shares one ribbon
 * color (blue) instead of splitting draft into its own group. */
function campaignGroupKey(status?: string): CampaignGroupKey {
  if (status === 'roster') return 'roster';
  if (status === 'draft' || status === 'private_draft' || status === 'in_progress') return 'active';
  if (status === 'completed') return 'completed';
  return 'other'; // template, trashed, unknown
}

/** Group display order — keeps each group contiguous with one ribbon header. */
const CAMPAIGN_GROUP_ORDER: Record<CampaignGroupKey, number> = {
  active: 0, completed: 1, other: 2, roster: 3,
};

/** Ribbon color + icon per group — matches the campaign status theme colors
 * (bg-ggc-blue = active, bg-ggc-green = completed, bg-ggc-sub-yellow = template,
 * bg-ggc-sub-skyblue = roster) so the sidebar ribbon doesn't clash with the
 * status badge shown elsewhere. */
const CAMPAIGN_GROUP_RIBBON: Record<CampaignGroupKey, { color: string; icon: string }> = {
  active: { color: 'bg-ggc-blue', icon: 'fa-folder' },
  completed: { color: 'bg-ggc-green', icon: 'fa-folder' },
  other: { color: 'bg-ggc-sub-yellow', icon: 'fa-folder' },
  roster: { color: 'bg-ggc-sub-skyblue', icon: 'fa-folder' },
};

export default function CampaignSelectDashboard({
  campaigns,
  campaignsLoading,
  selectedCampaignId,
  selectedCampaignLabel,
  onSelectCampaign,
  placeholder,
  showStatusFilter = true,
  excludeStatuses,
  noun,
  className,
  onAddRoster,
  onClearFilter,
  fullWidth,
}: CampaignSelectDashboardProps) {
  const { t } = useI18nText();
  const n = noun ?? t('campaigns', '캠페인');

  /* ── 드롭다운 열림/닫힘 ── */
  const [campaignDropOpen, setCampaignDropOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!campaignDropOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCampaignDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [campaignDropOpen]);

  /* ── 검색 / 필터 ── */
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<string | null>(null);
  const [showHiddenCampaigns, setShowHiddenCampaigns] = useState(false);

  // 캠페인 드롭다운이 열려있는 동안 사이드바 스크롤 차단
  useEffect(() => {
    if (!campaignDropOpen) return;
    const sidebar = dropdownRef.current?.closest('.dashboard-sidebar') as HTMLElement | null;
    if (!sidebar) return;
    sidebar.style.overflow = 'hidden';
    return () => { sidebar.style.overflow = ''; };
  }, [campaignDropOpen]);

  /* ── 최근 조회 캠페인 ── */
  const [recentCampaignIds, setRecentCampaignIds] = useState(getRecentCampaignIds);
  const [recentExpanded, setRecentExpanded] = useState(false);
  // Per-group expand/collapse. Active is expanded by default (the priority);
  // the larger/less-frequent groups start collapsed to keep the list scannable.
  const [groupExpanded, setGroupExpanded] = useState<Record<CampaignGroupKey, boolean>>({
    active: true, completed: false, other: false, roster: false,
  });
  const toggleGroup = useCallback((k: CampaignGroupKey) => {
    setGroupExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
  }, []);

  const addRecentCampaign = useCallback((id: string) => {
    setRecentCampaignIds(addRecentCampaignId(id));
  }, []);
  const removeRecentCampaign = useCallback((id: string) => {
    setRecentCampaignIds(removeRecentCampaignId(id));
  }, []);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c._id === selectedCampaignId),
    [campaigns, selectedCampaignId],
  );

  /* ── 파생 데이터 ── */
  const baseCampaignList = useMemo(() => {
    let list = campaigns;
    if (!showHiddenCampaigns) list = list.filter((c) => !c.hidden);
    if (excludeStatuses?.length) list = list.filter((c) => !excludeStatuses.includes(c.status ?? ''));
    return list;
  }, [campaigns, showHiddenCampaigns, excludeStatuses]);

  const campaignStatusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of baseCampaignList) {
      const s = c.status ?? '';
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [baseCampaignList]);

  const filteredCampaigns = useMemo(() => {
    let list = baseCampaignList;
    if (campaignStatusFilter) {
      list = list.filter((c) => c.status === campaignStatusFilter);
    }
    if (campaignSearch) {
      const q = campaignSearch.toLowerCase().trim();
      list = list.filter((c) => (c.name ?? '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      // Primary: group order (active → completed → other → roster) so each
      // group is contiguous and gets exactly one ribbon header.
      const ga = CAMPAIGN_GROUP_ORDER[campaignGroupKey(a.status)];
      const gb = CAMPAIGN_GROUP_ORDER[campaignGroupKey(b.status)];
      if (ga !== gb) return ga - gb;
      // Secondary: most recently updated first (replaces the old status-weight +
      // alphabetical tie-break — recency is more useful for picking a campaign).
      return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
    });
  }, [baseCampaignList, campaignSearch, campaignStatusFilter]);

  const groupCounts = useMemo(() => {
    const counts: Record<CampaignGroupKey, number> = { active: 0, completed: 0, other: 0, roster: 0 };
    for (const c of filteredCampaigns) counts[campaignGroupKey(c.status)] += 1;
    return counts;
  }, [filteredCampaigns]);
  const groupLabel: Record<CampaignGroupKey, string> = {
    active: t('Active', '액티브'),
    completed: t('Completed', '완료'),
    other: t('Templates', '템플릿'),
    roster: t('Rosters', '로스터'),
  };

  const handleCampaignSelect = useCallback((id: string) => {
    addRecentCampaign(id);
    onSelectCampaign(id);
    setCampaignDropOpen(false);
    setCampaignSearch('');
    setNameExpand(null);
  }, [addRecentCampaign, onSelectCampaign]);

  /* ── 캠페인명 확장 호버 ── */
  const [nameExpand, setNameExpand] = useState<{
    name: string; top: number; left: number; height: number;
  } | null>(null);

  const handleItemMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>, name: string) => {
    const nameEl = e.currentTarget.querySelector<HTMLElement>('.campaign-dropdown-name');
    if (nameEl && nameEl.scrollWidth > nameEl.offsetWidth) {
      const itemRect = e.currentTarget.getBoundingClientRect();
      const nameRect = nameEl.getBoundingClientRect();
      setNameExpand({ name, top: itemRect.top, left: nameRect.left, height: itemRect.height });
    } else {
      setNameExpand(null);
    }
  }, []);

  const handleItemMouseLeave = useCallback(() => setNameExpand(null), []);

  /* ── 키보드 네비게이션 ── */
  const [hlIdx, setHlIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const selectableIds = useMemo(() => {
    return filteredCampaigns.map((c) => c._id);
  }, [filteredCampaigns]);

  useEffect(() => { setHlIdx(-1); }, [campaignSearch, campaignStatusFilter]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setCampaignDropOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHlIdx((prev) => (prev < selectableIds.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHlIdx((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && hlIdx >= 0 && hlIdx < selectableIds.length) {
      e.preventDefault();
      handleCampaignSelect(selectableIds[hlIdx]);
    }
  }, [selectableIds, hlIdx, handleCampaignSelect]);

  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-campaign-idx="${hlIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [hlIdx]);

  return (
    <div className={`filter-section relative${className ? ` ${className}` : ''}`} ref={dropdownRef}>
      {/* Wrapper separates the clear × button from the trigger to avoid nested <button>.
          Its width must always track the trigger's rendered width — otherwise the
          absolutely-positioned × ends up floating past the trigger's visible edge. */}
      <div className={`relative flex items-stretch${fullWidth ? '' : ' max-w-[320px]'}`}>
        <button
          className={`filter-campaign-btn flex-1 min-w-0${fullWidth ? ' !max-w-none' : ''}${campaignDropOpen ? ' filter-campaign-btn--open' : ''}${onClearFilter && selectedCampaignId ? ' !pr-7' : ''}`}
          onClick={() => setCampaignDropOpen((v) => !v)}
        >
          {selectedCampaign && <CampaignStatusIcon status={selectedCampaign.status} />}
          <span className={`campaign-dropdown-name truncate${!selectedCampaignLabel ? ' text-ggc-text-gray-light' : ''}`}>
            {selectedCampaignLabel || placeholder || t('Select campaign or roster', '캠페인 or 로스터를 선택하세요')}
          </span>
          {!(onClearFilter && selectedCampaignId) && (
            <i className={`fa fa-caret-${campaignDropOpen ? 'up' : 'down'} ml-auto`} />
          )}
        </button>
        {onClearFilter && selectedCampaignId && (
          <button
            type="button"
            className="cfp-trigger__clear absolute right-1.5 top-1/2 -translate-y-1/2"
            onClick={(e) => { e.stopPropagation(); onClearFilter(); }}
            aria-label={t('Clear campaign filter', '캠페인 필터 해제')}
          >
            <i className="fa fa-times" />
          </button>
        )}
      </div>

      {campaignDropOpen && (
        <div className="campaign-dropdown">
          {/* 1행: 검색 + 숨김 토글 */}
          <div className="flex items-center gap-1.5 px-2 pt-2 pb-1">
            <div className="flex flex-1 items-center border border-ggc-border-gray-dark rounded-ggc-sm px-2 h-7 bg-white min-w-0">
              <i className="fa fa-search text-ggc-disabled mr-1.5 shrink-0 ggc-text-caption-s" />
              <input
                className="flex-1 min-w-0 text-xs outline-none bg-transparent"
                value={campaignSearch}
                onChange={(e) => setCampaignSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('search...', '검색...')}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <Tooltip content={showHiddenCampaigns ? t('Hide hidden campaigns', '숨긴 캠페인 감추기') : t('Show hidden campaigns', '숨긴 캠페인 표시')}>
              <button
                className={`campaign-dropdown-pill campaign-dropdown-pill--icon${showHiddenCampaigns ? ' campaign-dropdown-pill--active' : ''}`}
                onClick={() => setShowHiddenCampaigns((v) => !v)}
              >
                <i className="fa fa-eye-slash" />
              </button>
            </Tooltip>
          </div>
          {/* 2행: 상태 필터 */}
          {showStatusFilter && (
            <div className="px-2 pb-2 border-b border-ggc-border-gray">
              <StatusFilterToggles
                value={campaignStatusFilter}
                onChange={setCampaignStatusFilter}
                statusCounts={campaignStatusCounts}
                totalCount={baseCampaignList.length}
                excludeStatuses={excludeStatuses}
              />
            </div>
          )}

          {/* 캠페인 목록 */}
          <div className="campaign-dropdown-list" ref={listRef}>
            {campaignsLoading ? (
              <div className="drawer-loading">
                <i className="fa fa-spinner fa-spin drawer-loading__icon" />
                <span>{n} {t('loading...', '불러오는 중...')}</span>
              </div>
            ) : (
              <>
                {/* No filter (all) — shown when onClearFilter is provided */}
                {onClearFilter && (
                  <div
                    className={`cfp-item cfp-item--clear${!selectedCampaignId ? ' cfp-item--active' : ''}`}
                    onClick={() => { onClearFilter(); setCampaignDropOpen(false); }}
                  >
                    <i className="fa fa-times-circle mr-1" />
                    {t('No filter (all)', '전체 (필터 없음)')}
                  </div>
                )}
                {/* 최근 조회 캠페인 */}
                {recentCampaignIds.length > 0 && !campaignSearch && !campaignStatusFilter && (() => {
                  const recentCampaigns = recentCampaignIds
                    .map((id: string) => baseCampaignList.find((c: CampaignItem) => c._id === id))
                    .filter((c): c is CampaignItem => c != null)
                    .slice(0, 10);
                  if (recentCampaigns.length === 0) return null;
                  return (
                    <div className="bg-white">
                      <div className="px-2">
                        <Tooltip content={`${t('List of up to 10 items from your recent searches.', '최근 검색한 10개의')} ${n} ${t('list.', '목록입니다.')}`}>
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-white bg-orange-500 cursor-pointer select-none whitespace-nowrap shrink-0 ggc-text-badge"
                            style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 6px) 50%, 100% 100%, 0 100%)', borderRadius: '2px 0 0 2px' }}
                            onClick={() => setRecentExpanded((v) => !v)}
                          >
                            <i className={`fa fa-chevron-${recentExpanded ? 'down' : 'right'} text-[8px]`} />
                            <i className="fa fa-bookmark" />
                            {t('Recent', '최근 조회')} ({recentCampaigns.length})
                          </span>
                        </Tooltip>
                      </div>
                      {recentExpanded && recentCampaigns.map((c, idx) => (
                        <div
                          key={`recent-${c._id}`}
                          className={`campaign-dropdown-item${selectedCampaignId === c._id ? ' campaign-dropdown-item--active' : ''}`}
                          onClick={() => handleCampaignSelect(c._id)}
                          onMouseEnter={(e) => handleItemMouseEnter(e, c.name ?? '')}
                          onMouseLeave={handleItemMouseLeave}
                        >
                          <button
                            type="button"
                            className="campaign-dropdown-recent-remove"
                            onClick={(e) => { e.stopPropagation(); removeRecentCampaign(c._id); }}
                            aria-label={t('Remove from recent', '최근 목록에서 제거')}
                          >
                            <i className="fa fa-times" />
                          </button>
                          <span className="drawer-item-num">{idx + 1}</span>
                          <CampaignStatusIcon status={c.status} />
                          <span className="campaign-dropdown-name text-ggc-text-gray font-semibold">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {filteredCampaigns.map((c, idx) => {
                  const showGroups = !campaignSearch && !campaignStatusFilter;
                  const groupKey = campaignGroupKey(c.status);
                  const prevGroupKey = idx > 0 ? campaignGroupKey(filteredCampaigns[idx - 1]?.status) : null;
                  const showGroupHeader = showGroups && groupKey !== prevGroupKey;
                  const isHidden = showGroups && !groupExpanded[groupKey];
                  const ribbon = CAMPAIGN_GROUP_RIBBON[groupKey];
                  return (
                    <Fragment key={c._id}>
                      {showGroupHeader && (
                        <div className="px-2 py-0.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-white ${ribbon.color} cursor-pointer select-none whitespace-nowrap shrink-0 ggc-text-badge`}
                            style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 6px) 50%, 100% 100%, 0 100%)', borderRadius: '2px 0 0 2px' }}
                            onClick={() => toggleGroup(groupKey)}
                          >
                            <i className={`fa fa-chevron-${groupExpanded[groupKey] ? 'down' : 'right'} text-[8px]`} />
                            <i className={`fa ${ribbon.icon}`} />
                            {groupLabel[groupKey]} ({groupCounts[groupKey]})
                            <Tooltip essential content={t('Sorted by last updated', '최근 업데이트순으로 정렬됨')}>
                              <span className="inline-flex items-center gap-0.5 opacity-80 ml-0.5">
                                <i className="fa fa-sort-amount-desc text-[10px]" />
                                <span className="ggc-text-caption-s">{t('Newest', '최신순')}</span>
                              </span>
                            </Tooltip>
                          </span>
                        </div>
                      )}
                      {!isHidden && (
                        <div
                          data-campaign-idx={idx}
                          className={`campaign-dropdown-item${selectedCampaignId === c._id ? ' campaign-dropdown-item--active' : ''}${hlIdx === idx ? ' campaign-dropdown-item--highlight' : ''}`}
                          onClick={() => handleCampaignSelect(c._id)}
                          onMouseEnter={(e) => handleItemMouseEnter(e, c.name ?? '')}
                          onMouseLeave={handleItemMouseLeave}
                        >
                          <CampaignStatusIcon status={c.status} />
                          <span className="campaign-dropdown-name">{c.name}</span>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </>
            )}
            {!campaignsLoading && filteredCampaigns.length === 0 && (
              <div className="campaign-dropdown-empty">
                {n} {t('not found', '을(를) 찾을 수 없습니다')}
              </div>
            )}
          </div>

          {/* New roster 고정 버튼 */}
          {!campaignSearch && !campaignStatusFilter && onAddRoster && (
            <>
              <div className="campaign-dropdown-divider" />
              <button
                type="button"
                className="campaign-dropdown-item mr-1 text-ggc-cta-blue hover:bg-ggc-blue-light"
                onClick={() => { onAddRoster(); setCampaignDropOpen(false); }}
              >
                <i className="fa fa-plus mr-2 w-3.5 text-center" />
                <span className="campaign-dropdown-name">{t('New roster', '새 로스터 생성')}</span>
              </button>
            </>
          )}

          {/* 하단 건수 */}
          <div className="campaign-dropdown-footer">
            {campaignsLoading
              ? `${n} ${t('loading...', '불러오는 중...')}`
              : `${filteredCampaigns.length !== baseCampaignList.length
                ? `${filteredCampaigns.length} / ${baseCampaignList.length}`
                : `${baseCampaignList.length}`
              } ${n}`}
          </div>
        </div>
      )}
      {nameExpand && createPortal(
        <div
          style={{
            position: 'fixed',
            top: nameExpand.top,
            left: nameExpand.left,
            height: nameExpand.height,
            display: 'flex',
            alignItems: 'center',
            background: 'var(--ggc-guide-blue-bg, #d8e0f8)',
            whiteSpace: 'nowrap',
            fontSize: 12,
            color: 'var(--ggc-cta-blue, #2528eb)',
            fontWeight: 600,
            paddingRight: 12,
            zIndex: 9999,
            pointerEvents: 'none',
            borderRadius: '0 3px 3px 0',
            boxShadow: '4px 0 8px rgba(37,40,235,0.12)',
          }}
        >
          {nameExpand.name}
        </div>,
        document.body,
      )}
    </div>
  );
}

